import React from 'react';
import { 
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Button,
  Space,
  message
} from 'antd';
import { 
  UserOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';
import { useUpdatePatient, useCreatePatient } from '../../hooks/useGraphQL';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;



interface PatientEditModalProps {
  visible: boolean;
  patient?: any | null;
  onCancel: () => void;
  onSuccess?: (patient: any) => void;
  mode?: 'create' | 'edit';
}

const PatientEditModal: React.FC<PatientEditModalProps> = ({
  visible,
  patient,
  onCancel,
  onSuccess,
  mode = 'edit'
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [updatePatient, { loading: isUpdating }] = useUpdatePatient();
  const [createPatient, { loading: isCreating }] = useCreatePatient();

  const handleSaveEdit = async () => {
    try {
      const values = await form.validateFields();
      
      if (mode === 'create') {
        const result = await createPatient({
          variables: {
            createPatientDto: {
              ...values,
              birthdate: values.birthdate?.format('YYYY-MM-DD'),
              tenant: 'default'
            }
          }
        });

        if (result.data?.createPatient) {
          message.success(t('patients.patientCreatedSuccessfully'));
          onSuccess?.(result.data.createPatient);
          onCancel();
        }
      } else {
        if (!patient?.id) {
          message.error('Patient ID is required');
          return;
        }

        const result = await updatePatient({
          variables: {
            id: patient.id,
            updatePatientDto: {
              first_name_en: values.first_name_en,
              first_name_zh_hk: values.first_name_zh_hk,
              last_name_en: values.last_name_en,
              last_name_zh_hk: values.last_name_zh_hk,
              email: values.email,
              phone_number: values.phone_number,
              birthdate: values.birthdate?.format('YYYY-MM-DD'),
              gender: values.gender,
              nationality: values.nationality,
              relationship_to_owner: values.relationship_to_owner,
              identity_document_type: values.identity_document_type,
              identity_document_number: values.identity_document_number,
              blood_type: values.blood_type,
              language_preferences: values.language_preferences,
              emergency_contact_name: values.emergency_contact_name,
              emergency_contact_phone: values.emergency_contact_phone,
              notes: values.notes,
            }
          }
        });

        if (result.data?.updatePatient) {
          message.success(t('patients.patientUpdatedSuccessfully'));
          onSuccess?.(result.data.updatePatient);
          onCancel();
        }
      }
    } catch (error) {
      console.error('Error saving patient:', error);
      message.error(mode === 'create' ? t('patients.errorCreatingPatient') : t('patients.errorUpdatingPatient'));
    }
  };

  const handleCancelEdit = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
               title={
           <div style={{ display: 'flex', alignItems: 'center' }}>
             <UserOutlined style={{ marginRight: 8, color: '#1890ff' }} />
             {mode === 'create' ? t('patients.addNewPatient') : t('patients.editPatient')}
           </div>
         }
      open={visible}
      onCancel={handleCancelEdit}
      footer={null}
      destroyOnClose
      width={800}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          first_name_en: patient?.first_name_en,
          first_name_zh_hk: patient?.first_name_zh_hk,
          last_name_en: patient?.last_name_en,
          last_name_zh_hk: patient?.last_name_zh_hk,
          email: patient?.email,
          phone_number: patient?.phone_number,
          birthdate: patient?.birthdate ? dayjs(patient.birthdate) : undefined,
          relationship_to_owner: patient?.relationship_to_owner,
          gender: patient?.gender,
          nationality: patient?.nationality,
          language_preferences: patient?.language_preferences,
          identity_document_type: patient?.identity_document_type,
          identity_document_number: patient?.identity_document_number,
          blood_type: patient?.blood_type,
          emergency_contact_name: patient?.emergency_contact_name,
          emergency_contact_phone: patient?.emergency_contact_phone,
          notes: patient?.notes,
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="first_name_en"
              label={t('patients.englishFirstName')}
              rules={[{ required: true, message: t('patients.pleaseInputEnglishFirstName') }]}
            >
              <Input placeholder={t('patients.englishFirstNamePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="first_name_zh_hk"
              label={t('patients.chineseFirstName')}
              rules={[{ required: true, message: t('patients.pleaseInputChineseFirstName') }]}
            >
              <Input placeholder={t('patients.chineseFirstNamePlaceholder')} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="last_name_en"
              label={t('patients.englishLastName')}
              rules={[{ required: true, message: t('patients.pleaseInputEnglishLastName') }]}
            >
              <Input placeholder={t('patients.englishLastNamePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="last_name_zh_hk"
              label={t('patients.chineseLastName')}
              rules={[{ required: true, message: t('patients.pleaseInputChineseLastName') }]}
            >
              <Input placeholder={t('patients.chineseLastNamePlaceholder')} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="email"
              label={t('patients.email')}
              rules={[{ type: 'email', message: t('patients.pleaseEnterValidEmail') }]}
            >
              <Input placeholder={t('patients.emailPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="phone_number"
              label={t('patients.phoneNumber')}
            >
              <Input placeholder={t('patients.phoneNumberPlaceholder')} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="birthdate"
              label={t('patients.dateOfBirth')}
              rules={[{ required: true, message: t('patients.pleaseSelectDateOfBirth') }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="gender"
              label={t('patients.gender')}
              rules={[{ required: true, message: 'Please select gender!' }]}
            >
              <Select>
                <Option value="male">{t('patients.male')}</Option>
                <Option value="female">{t('patients.female')}</Option>
                <Option value="other">{t('patients.other')}</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="nationality"
              label={t('patients.nationality')}
            >
              <Input placeholder={t('patients.nationalityPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="relationship_to_owner"
              label={t('patients.relationships.self')}
            >
              <Select>
                <Option value="self">{t('patients.relationships.self')}</Option>
                <Option value="spouse">{t('patients.relationships.spouse')}</Option>
                <Option value="child">{t('patients.relationships.child')}</Option>
                <Option value="parent">{t('patients.relationships.parent')}</Option>
                <Option value="sibling">{t('patients.relationships.sibling')}</Option>
                <Option value="other">{t('patients.relationships.other')}</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="identity_document_type"
              label={t('patients.idDocumentType')}
            >
              <Select placeholder={t('patients.idDocumentTypePlaceholder')}>
                <Option value="hkid">HKID</Option>
                <Option value="passport">Passport</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="identity_document_number"
              label={t('patients.idDocumentNumber')}
            >
              <Input placeholder={t('patients.idDocumentNumberPlaceholder')} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="blood_type"
              label={t('patients.bloodType')}
            >
              <Input placeholder={t('patients.bloodTypePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="language_preferences"
              label={t('patients.languagePreferences')}
            >
              <Select mode="tags" tokenSeparators={[',']} placeholder={t('languages.selectLanguages')}>
                <Option value="en">{t('languages.en')}</Option>
                <Option value="zh-hk">{t('languages.zh-hk')}</Option>
                <Option value="zh-cn">{t('languages.zh-cn')}</Option>
                <Option value="yue">{t('languages.yue')}</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="emergency_contact_name"
              label={t('patients.emergencyContactName')}
            >
              <Input placeholder={t('patients.emergencyContactNamePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="emergency_contact_phone"
              label={t('patients.emergencyContactPhone')}
            >
              <Input placeholder={t('patients.emergencyContactPhonePlaceholder')} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="notes"
          label={t('patients.notes')}
        >
          <TextArea rows={4} placeholder={t('patients.notesPlaceholder')} />
        </Form.Item>

        <div style={{ textAlign: 'right', marginTop: 24 }}>
          <Space>
            <Button onClick={handleCancelEdit} disabled={isUpdating}>
              {t('common.cancel')}
            </Button>
                           <Button 
                 type="primary" 
                 onClick={handleSaveEdit}
                 loading={isUpdating || isCreating}
                 icon={<SaveOutlined />}
               >
                 {mode === 'create' ? t('patients.createPatient') : t('patients.updatePatient')}
               </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

export default PatientEditModal;
